import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import { restartApp } from "@-/dev.improve.haniverse.lib.app.restart";
import ensureError from "@-/com.npmjs.ensure-error";
import { Component, type ReactNode } from "react";
import { Pressable, ScrollView, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";

/**
 * Catches render-time errors in the provider tree and renders them visibly instead of
 * leaving the user with a permanent splash. Without this, a thrown error inside any
 * provider can leave the native bootsplash never hidden.
 *
 * Kept in its own package so Fast Refresh of `_layout.tsx` doesn't reassign this class to
 * a fresh identity each time and force the whole React tree to unmount.
 */
export class StartupErrorBoundary extends Component<
  { children: ReactNode },
  { areDetailsVisible: boolean; error: Error | null; restartFailed: boolean }
> {
  state: { areDetailsVisible: boolean; error: Error | null; restartFailed: boolean } = {
    areDetailsVisible: false,
    error: null,
    restartFailed: false,
  };

  static getDerivedStateFromError(error: unknown): { error: Error } {
    return { error: ensureError(error) };
  }

  componentDidCatch(error: unknown, info: { componentStack?: string | null }): void {
    logger(import.meta.location).error(
      "[startup] React error boundary caught error during root layout",
      error,
      info.componentStack ?? "(no stack)",
    );
    // A recovered error boundary is NOT an unhandled exception, so Sentry's RN
    // integration never auto-captures it — a startup crash that lands here (e.g. a
    // missing env var throwing during the first render) would otherwise be invisible
    // in Sentry despite the app being visibly broken. Report it explicitly.
    captureSentryException(ensureError(error), {
      contexts: { react: { componentStack: info.componentStack ?? "(no stack)" } },
      level: "fatal",
      tags: { "startup.error_boundary": "true" },
    });
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) {
      return this.props.children;
    }
    return (
      <View className="flex-1 gap-3 bg-background p-6 pt-15">
        <Text className="text-base font-bold">Something went wrong</Text>
        <Text className="opacity-70">Try again to reopen Haniverse, or restart the app.</Text>
        <Pressable
          accessibilityRole="button"
          className="items-center rounded-xs bg-foreground px-5 py-3"
          onPress={() => {
            this.setState({ areDetailsVisible: false, error: null, restartFailed: false });
          }}
        >
          <Text className="font-semibold text-background">Try again</Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          className="items-center rounded-xs border border-foreground/20 px-5 py-3"
          onPress={() => {
            this.setState({ restartFailed: false });
            void restartApp().catch((restartError: unknown) => {
              logger(import.meta.location).error("App restart failed", restartError);
              this.setState({ restartFailed: true });
            });
          }}
        >
          <Text className="font-semibold">Restart app</Text>
        </Pressable>
        {this.state.restartFailed ? (
          <Text>Restart did not finish. Try again, or close and reopen Haniverse.</Text>
        ) : null}
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            this.setState(({ areDetailsVisible }) => ({ areDetailsVisible: !areDetailsVisible }));
          }}
        >
          <Text className="text-sm underline opacity-60">
            {this.state.areDetailsVisible ? "Hide details" : "Show details"}
          </Text>
        </Pressable>
        {this.state.areDetailsVisible ? (
          <ScrollView className="mt-2 flex-1">
            <Text className="font-mono text-[11px] opacity-60" selectable>
              {error.stack ?? error.message}
            </Text>
          </ScrollView>
        ) : null}
      </View>
    );
  }
}
